import * as THREE from 'three';

export type Reaction = 'speaking' | 'ready' | 'listening' | 'holding' | 'hot' | 'burning' | 'pleased';
/** Rigid faces and authored morphs; no speech inference or lip sync. */
export function createPotatoActor(model: THREE.Object3D, clips: THREE.AnimationClip[] = [], host = false) {
  const eyes: { object: THREE.Object3D; scale: THREE.Vector3; position: THREE.Vector3 }[] = [];
  const morphs: THREE.Mesh[] = [];
  model.traverse(object => {
    if (object.name.startsWith('Eye')) eyes.push({ object, scale: object.scale.clone(), position: object.position.clone() });
    if (object instanceof THREE.Mesh && object.morphTargetDictionary) morphs.push(object);
  });
  const basePosition = model.position.clone(); const baseRotation = model.rotation.clone(); const baseScale = model.scale.clone();
  const mixer = clips.length ? new THREE.AnimationMixer(model) : null;
  let action: THREE.AnimationAction | undefined; let gesture = ''; let previousTime: number | undefined;
  return {
    update(reaction: Reaction, seconds: number, seed: number, reducedMotion: boolean, attention: number) {
      const delta = previousTime === undefined ? 0 : Math.min(.1, Math.max(0, seconds - previousTime)); previousTime = seconds;
      const nextGesture = reaction === 'speaking' ? 'speaking' : reaction === 'pleased' ? 'pleased' : reaction === 'burning' ? 'concerned' : reaction === 'hot' ? 'surprised' : reaction === 'ready' ? 'idle' : 'listening';
      if (mixer && gesture !== nextGesture) {
        const clip = THREE.AnimationClip.findByName(clips, nextGesture);
        if (clip) { action?.fadeOut(.25); action = mixer.clipAction(clip); action.reset().fadeIn(.25).play(); }
        gesture = nextGesture;
      }
      if (mixer) { if (reducedMotion) { if (action) { action.stopFading().setEffectiveWeight(1); action.time = .65; } mixer.update(0); } else mixer.update(delta); }
      const phase = (seconds + seed * .79) % (3.4 + seed * .23);
      const blink = reducedMotion ? 1 : phase < .16 ? Math.max(.08, Math.abs(phase - .08) / .08) : 1;
      const tense = reaction === 'burning'; const surprise = reaction === 'hot';
      eyes.forEach(({object, scale, position}, i) => {
        const height = surprise ? 1.35 : tense ? (host ? .55 : 1.16) : reaction === 'listening' ? (i ? .82 : 1) : 1;
        object.scale.copy(scale).multiply(new THREE.Vector3(surprise ? 1.15 : 1, height * blink, 1));
        object.position.copy(position); object.position.x += attention * .018;
        object.rotation.z = host && tense ? (i ? -.2 : .2) : 0;
      });
      for (const mesh of morphs) {
        const dictionary = mesh.morphTargetDictionary!; const weights = mesh.morphTargetInfluences!;
        for (const name of ['happy', 'flat', 'open', 'wavy']) {
          const index = dictionary[name]; if (index === undefined) continue;
          const target = name === 'happy' ? (reaction === 'pleased' ? 1 : 0)
            : name === 'open' ? (surprise ? .95 : tense ? .65 : 0)
            : name === 'wavy' ? (tense ? .3 : 0)
            : reaction === 'listening' ? .6 : 0;
          weights[index] = reducedMotion ? target : THREE.MathUtils.damp(weights[index] ?? 0, target, 12, delta);
        }
      }
      model.position.copy(basePosition); model.rotation.copy(baseRotation); model.scale.copy(baseScale);
      model.rotation.y += attention * .16;
      model.rotation.z += reaction === 'listening' ? .035 : tense ? -.055 : 0;
      if (!reducedMotion) {
        model.position.y += Math.sin(seconds * 2 + seed) * .009;
        if (tense && !host) model.rotation.z += Math.sin(seconds * 8 + seed) * .035;
        if (reaction === 'holding') model.rotation.x += Math.sin(seconds * 2.8) * .035;
        if (reaction === 'pleased' && !host) model.scale.y *= 1 + Math.sin(seconds * 3.5 + seed) * .025;
      }
    },
    dispose() { mixer?.stopAllAction(); mixer?.uncacheRoot(model); },
  };
}
